import { useStaticQuery, graphql } from 'gatsby';
import React, { useEffect, useState, useMemo } from 'react';
import styled, {
  x,
  css,
  up,
  down,
  th,
  useUp
} from '@xstyled/styled-components';
import { Dialog, DialogDisclosure } from 'reakit/Dialog';
import { Portal } from 'reakit/Portal';
import { RiPencilLine } from 'react-icons/ri';
import { ScreenContainer } from 'smooth-doc/components';
import {
  SideNav,
  useSideNavState,
  useSideNavPrevNext
} from 'smooth-doc/src/components/SideNav';
import { TableOfContents } from './TableOfContents';
import { SiblingNav, SiblingNavLink } from './SiblingNav';
import { Article } from './Article';
import { PageLayout } from './PageLayout';
import { useSidenavDialog } from '../context/SidenavDialogContext';

const PageLayoutQuery = graphql`
  query PageLayoutQuery {
    site {
      siteMetadata {
        latestVersionSiteUrl
        version
      }
    }
  }
`;

const SidebarDialog = styled.div`
  background-color: #f1f4f7;
  backdrop-filter: blur(3px);
  position: fixed;
  top: 49;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 30;
  overflow: auto;
  transition: base;
  opacity: 0;
  padding-bottom: 60px;

  &[data-enter] {
    opacity: 1;
  }

  &:focus {
    outline: none;
  }
`;

const Container = styled.div`
  z-index: 0;
  position: relative;
  height: 100%;

  ${up(
    'lg',
    css`
      display: grid;
      grid-template-columns: 340px minmax(0, 1fr);
      grid-gap: ${th.space(8)};

      .sidebar-container {
        display: none;
      }
    `
  )}
  ${up(
    'xl',
    css`
      grid-template-columns: 340px minmax(0, 1fr) 340px;

      .sidebar-container {
        display: none;
      }
    `
  )};
`;

const TocContainer = styled.div`
  ${down(
    'xl',
    css`
      display: none;
    `
  )}
`;

const SidebarSticky = styled.aside`
  position: sticky;
  top: ${th.px(70)};
  padding: 4 0;
  overflow-y: auto;
  width: 340px;
  border-right: 1px solid #7b99a1;
  background: #f1f4f7;

  ${down(
    'lg',
    css`
      display: none;
    `
  )};
`;

const MenuButton = styled.a`
  appearance: none;
  border: 0;
  border-radius: 50%;
  width: 56;
  height: 56;
  position: fixed;
  right: ${th.size(8)};
  bottom: ${th.size(100)};
  z-index: 25;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: background;
  background-color: #4ca48a;

  &:focus {
    color: background;
  }

  > svg {
    width: 24;
    height: 24;
  }

  &[aria-expanded='true'] {
    > svg:first-child {
    }
  }
`;

const VersionNote = styled.div`
  font-size: 16;
  background-color: blockquote-background;
  padding: 4 5;
  margin: 4 0;
  border-radius: base;
  border: 1px solid #e3720e;
  line-height: 1.5;
  a {
    transition: fast;
    color: blockquote-link;
    text-decoration: underline;

    &:hover {
      color: on-background;
    }
  }
`;

function MobileSidebar({ children }) {
  const dialog = useSidenavDialog();
  return (
    <>
      <Dialog {...dialog} as={SidebarDialog} preventBodyScroll>
        {children}
      </Dialog>
      <Portal>
        <DialogDisclosure
          as={MenuButton}
          href="https://www.axiomatics.com/contact-us/"
          target="_blank"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 4H20V16H5.17L4 17.17V4ZM4 2C2.9 2 2.01 2.9 2.01 4L2 22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2H4ZM6 12H14V14H6V12ZM6 9H18V11H6V9ZM6 6H18V8H6V6Z"
              fill="#F3F5F8"
            />
          </svg>
        </DialogDisclosure>
      </Portal>
    </>
  );
}

function PrevNextLinks(props) {
  const { prev, next } = useSideNavPrevNext(props);
  if (!prev && !next) return null;
  return (
    <SiblingNav>
      {prev && (
        <SiblingNavLink type="previous" to={prev.fields.slug}>
          {prev.fields.title}
        </SiblingNavLink>
      )}
      {next && (
        <SiblingNavLink type="next" to={next.fields.slug}>
          {next.fields.title}
        </SiblingNavLink>
      )}
    </SiblingNav>
  );
}

export function DocLayout({
  children,
  tableOfContents,
  editLink,
  path,
  ...props
}) {
  const upLg = useUp('lg');

  const {
    site: {
      siteMetadata: { version, latestVersionSiteUrl }
    }
  } = useStaticQuery(PageLayoutQuery);

  const [versions, setVersions] = useState(null);

  useEffect(() => {
    const fetchverions = async () => {
      const response = await fetch(`${latestVersionSiteUrl}/versions.json`);
      const versions = await response.json(); // ['1.0.0']; //
      setVersions(versions);
    };
    fetchverions();
  }, [latestVersionSiteUrl]);

  const sideNav = useSideNavState(versions, version, latestVersionSiteUrl);

  const isLatestVersion = useMemo(() => {
    if (versions) {
      const [latestVersion] = versions;
      return latestVersion === version;
    }
    return true;
  }, [versions, version]);

  return (
    <PageLayout
      mainCSS={
        upLg
          ? {
              background:
                'linear-gradient(90deg, #f1f4f7 calc(50vw - 390px), #ffffff calc(50vw - 389px), #ffffff 100vw)'
            }
          : null
      }
      {...props}
    >
      <ScreenContainer px={0}>
        <Container>
          <SidebarSticky>
            <SideNav {...sideNav} />
          </SidebarSticky>
          <div className="sidebar-container">
            {!upLg && (
              <MobileSidebar>
                <SideNav {...sideNav} />
              </MobileSidebar>
            )}
          </div>
          <x.div pb={6} px={3}>
            {!isLatestVersion && (
              <VersionNote role="alert">
                This is documentation for ALFA <b>{version}</b>, which is no
                longer actively maintained.
                <br />
                For up-to-date documentation, see the{' '}
                <b>
                  <a href={latestVersionSiteUrl}>latest version</a>
                </b>{' '}
                ({versions[0]}).
              </VersionNote>
            )}
            <Article>
              {children}
              {editLink && (
                <x.a
                  mt={5}
                  display="grid"
                  gridTemplateColumns="min-content max-content"
                  gridGap={1}
                  alignItems="center"
                  href={editLink}
                >
                  <RiPencilLine /> Edit this page on GitHub
                </x.a>
              )}
              <PrevNextLinks {...sideNav} />
            </Article>
          </x.div>
          <TocContainer>
            <TableOfContents />
          </TocContainer>
        </Container>
      </ScreenContainer>
    </PageLayout>
  );
}
